import { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import dayjs from 'dayjs';

import { BreadcrumbHeader } from '../../components/shared';
import { purchaseOrders, supplierPayments } from '../../services/dataClient';
import '../../styles/design-tokens.css';

import PaymentsTab from './tabs/PaymentsTab';

// ─── Constants ────────────────────────────────────────────────────────────────
import ContentCard from "../../components/shared/ContentCard";
function computeRange(preset, customStart, customEnd) {
  const today = dayjs();
  switch (preset) {
    case 'thisMonth':return { start: today.startOf('month').format('YYYY-MM-DD'), end: today.endOf('month').format('YYYY-MM-DD') };
    case 'lastMonth':{
        const lm = today.subtract(1, 'month');
        return { start: lm.startOf('month').format('YYYY-MM-DD'), end: lm.endOf('month').format('YYYY-MM-DD') };
      }
    case 'last3':return { start: today.subtract(3, 'month').format('YYYY-MM-DD'), end: today.format('YYYY-MM-DD') };
    case 'last6':return { start: today.subtract(6, 'month').format('YYYY-MM-DD'), end: today.format('YYYY-MM-DD') };
    case 'ytd':return { start: today.startOf('year').format('YYYY-MM-DD'), end: today.format('YYYY-MM-DD') };
    case 'custom':
      return {
        start: customStart || today.startOf('month').format('YYYY-MM-DD'),
        end: customEnd || today.format('YYYY-MM-DD')
      };
    default:return { start: today.startOf('month').format('YYYY-MM-DD'), end: today.format('YYYY-MM-DD') };
  }
}

const inRange = (dateStr, start, end) =>
Boolean(dateStr) && dateStr >= start && dateStr <= end;

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function PaymentsPage() {
  const navigate = useNavigate();

  const [searchParams, setSearchParams] = useSearchParams();
  const preset = searchParams.get('preset') ?? 'thisMonth';
  const customStart = searchParams.get('customStart') ?? '';
  const customEnd = searchParams.get('customEnd') ?? '';

  const setParam = (key, value) =>
  setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    if (value) next.set(key, value);else next.delete(key);
    return next;
  }, { replace: true });

  const setPreset = (v) => setParam('preset', v === 'thisMonth' ? '' : v);
  const setCustomStart = (v) => setParam('customStart', v);
  const setCustomEnd = (v) => setParam('customEnd', v);

  const [payments, setPayments] = useState(supplierPayments);
  const [pos] = useState(purchaseOrders);

  const { start: startDate, end: endDate } = useMemo(
    () => computeRange(preset, customStart, customEnd),
    [preset, customStart, customEnd]
  );

  const filteredPayments = useMemo(
    () => payments.filter((p) => inRange(p.dueDate, startDate, endDate)),
    [payments, startDate, endDate]
  );

  // Navigate to the PO detail page when clicking a linked PO
  const handleSwitchToPo = (poNumber) => {
    const po = pos.find((p) => p.poNumber === poNumber);
    if (po) navigate(`/accounting/purchase-orders/${po.id}`);
  };

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header */}
      <BreadcrumbHeader
        title="Payments"
        crumbs={[
        { label: 'Accounting' },
        { label: 'Payments' }]
        } />

      {/* Content card */}
      <ContentCard
        sx={{ p: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}>
        
        <PaymentsTab
          payments={filteredPayments}
          allPayments={payments}
          onUpdate={setPayments}
          allPos={pos}
          onSwitchToPo={handleSwitchToPo}
          preset={preset}
          onPresetChange={setPreset}
          customStart={customStart}
          onCustomStartChange={setCustomStart}
          customEnd={customEnd}
          onCustomEndChange={setCustomEnd} />
        
      </ContentCard>

    </Box>);

}